import type { FC } from 'react';
import Box from 'src/components-v2/Layout/Box/Box';
import { RichTextSwitch } from 'src/containers';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import { type TDesignSystemFontStyle } from 'src/lib/design-system/typography';
import type {
  DecorativeStatEntry,
  DecorativeStatSectionShape,
} from 'src/types/contentful';
import { useTheme } from 'threads5/styles/useTheme';
import ThemeProvider from 'threads5/themes/theme-provider';

const DecorativeStatAppearanceMap: Record<
  DecorativeStatSectionShape['appearance'],
  TDesignSystemFontStyle
> = {
  Small: 'S Stat',
  Medium: 'M Stat',
  Large: 'L Stat',
};

const DecorativeStatH3MarginBottomMap: Record<
  DecorativeStatSectionShape['appearance'],
  string
> = {
  // These compensate for the stat line box being shorter than its natural
  // typographic height: each value holds margin + (lineHeight - fontSize) / 2
  // constant at desktop so the stat-to-label gap matches the approved layout.
  // EM is used instead of REM to scale relative to dynamic font sizes ("L Stat").
  Small: '0.1875em',
  Medium: '0.05em',
  Large: '0.035em',
};

const DecorativeStat: FC<
  DecorativeStatEntry & {
    layout: DecorativeStatSectionShape['layout'];
    appearance: DecorativeStatSectionShape['appearance'];
    mobileAppearance?: DecorativeStatSectionShape['mobileAppearance'];
  }
> = ({
  fields: { body, styles },
  layout,
  appearance,
  mobileAppearance,
  sys,
}) => {
  // Design spec mandates decorative gradient text use light-mode style token gradients regardless
  // of parent theme. Here, we're pulling the parent theme and applying it to specific nodes,
  // while wrapping the entire component in a forced-light mode theme provider.
  const parentTheme = useTheme();
  const parentColorStyles = {
    color: parentTheme.palette['web3/text/contrast-max'],
  };

  const lightModeTheme = useComponentTheme('light');
  const isMobileStackedCentered =
    mobileAppearance === 'Stacked' && layout === 'Centered';

  return (
    <ThemeProvider theme={lightModeTheme}>
      <Box
        className='cms-decorative-stat'
        display='flex'
        flexDirection='column'
        sx={{
          img: {
            mb: 3,
          },
          textAlign: {
            xs: isMobileStackedCentered ? 'center' : 'left',
            sm: layout === 'Centered' ? 'center' : 'left',
          },
          alignItems: {
            xs: isMobileStackedCentered ? 'center' : 'flex-start',
            sm: layout === 'Centered' ? 'center' : 'flex-start',
          },
          ...styles,
        }}
      >
        <RichTextSwitch
          sys={sys}
          content={body}
          nodes={{
            h3: {
              variant: DecorativeStatAppearanceMap[appearance],
              component: 'h3',
              fieldId: 'body',
              theme: 'web 3.0',
              sx: {
                ...parentColorStyles,
                mt: 0,
                mb: DecorativeStatH3MarginBottomMap[appearance],
              },
            },
            h4: {
              variant:
                appearance === 'Large' ? 'Subhead SemiBold' : 'P1 SemiBold',
              component: 'h4',
              fieldId: 'body',
              theme: 'web 3.0',
              sx: {
                ...parentColorStyles,
                mt: 0,
                mb: '1.2rem',
              },
            },
            p: {
              variant: appearance === 'Small' ? 'P2 Regular' : 'P1 Regular',
              component: 'p',
              fieldId: 'body',
              theme: 'web 3.0',
              sx: {
                ...parentColorStyles,
                my: 0,
                maxWidth: '580px',
                textWrap: 'balance',
              },
            },
          }}
        />
      </Box>
    </ThemeProvider>
  );
};

export default DecorativeStat;
